import { Button, Icon } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { useRelativeTime } from "@/features/opportunities/lib/format-relative-time.js";

import { personaOpportunitySetDisplayTimestamp } from "../../../lib/persona-opportunity-set-mapper.js";
import type { PersonaOpportunitySet } from "../../../types/persona-opportunities.types.js";
import { PersonaOpportunityExportDropdown } from "../PersonaOpportunityExportDropdown/PersonaOpportunityExportDropdown.js";
import { PersonaOpportunitySetPicker } from "../PersonaOpportunitySetPicker/PersonaOpportunitySetPicker.js";

export interface PersonaOpportunitySetHeaderProps {
  selectedSet: PersonaOpportunitySet;
  sets: PersonaOpportunitySet[];
  canCreateSet: boolean;
  onBack: () => void;
  onSelectSet: (set: PersonaOpportunitySet) => void;
  onCreateSet?: () => void;
  /** Opens regenerate dialog — READY + GENERATE only. */
  onRegenerate?: () => void;
  canImport?: boolean;
  onImport?: () => void;
  exportFiltersActive?: boolean;
  onExportAll?: () => void;
  onExportFiltered?: () => void;
}

export function PersonaOpportunitySetHeader({
  selectedSet,
  sets,
  canCreateSet,
  onBack,
  onSelectSet,
  onCreateSet,
  onRegenerate,
  canImport = false,
  onImport,
  exportFiltersActive = false,
  onExportAll,
  onExportFiltered,
}: PersonaOpportunitySetHeaderProps) {
  const { t } = useTranslation();
  const relativeTime = useRelativeTime(personaOpportunitySetDisplayTimestamp(selectedSet));
  const showGeneratedAge = selectedSet.status === "READY";
  const canRegenerate = showGeneratedAge && canCreateSet && !!onRegenerate;
  const showImport = canImport && !!onImport;
  const showExport = canCreateSet && !!onExportAll && !!onExportFiltered;
  const showCreate = canCreateSet && !!onCreateSet;

  return (
    <div className="flex shrink-0 flex-wrap items-start gap-4">
      <button
        type="button"
        onClick={onBack}
        className="mt-0.5 flex size-11 shrink-0 items-center justify-center rounded-sm border border-input-border bg-background text-foreground transition-colors hover:bg-muted [@media(hover:hover)_and_(pointer:fine)]:size-8"
        aria-label={t("personaOpportunities.allSets")}
      >
        <Icon name="chevron-left" size="md" color="inherit" />
      </button>

      <div className="min-w-0">
        <PersonaOpportunitySetPicker
          sets={sets}
          selectedSet={selectedSet}
          onSelect={onSelectSet}
          onSeeAll={onBack}
        />

        {showGeneratedAge ? (
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              {t("personaOpportunities.workspace.generatedAgo", {
                time: relativeTime,
                defaultValue: `Generated ${relativeTime}`,
              })}
            </span>
            <span className="text-input-border" aria-hidden>
              ·
            </span>
            {canRegenerate ? (
              <button
                type="button"
                onClick={onRegenerate}
                className="inline-flex min-h-11 items-center gap-1 text-primary transition-colors hover:underline [@media(hover:hover)_and_(pointer:fine)]:min-h-0"
              >
                <Icon name="refresh-cw" size="xs" color="inherit" />
                {t("personaOpportunities.workspace.regenerate", "Regenerate")}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {showImport || showExport || showCreate ? (
        <div className="ml-auto flex shrink-0 items-center gap-2.5">
          {showImport ? (
            <Button type="button" variant="outline" iconStart="upload" onClick={onImport}>
              {t("personaOpportunities.scripts.import.button", "Import")}
            </Button>
          ) : null}
          {showExport ? (
            <PersonaOpportunityExportDropdown
              canExport
              filtersActive={exportFiltersActive}
              onExportAll={onExportAll}
              onExportFiltered={onExportFiltered}
            />
          ) : null}
          {showCreate ? (
            <Button type="button" className="font-semibold" iconStart="plus" onClick={onCreateSet}>
              {t("personaOpportunities.workspace.createSet", "Create set")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
